import { CommandBarButton, IContextualMenuProps } from "@fluentui/react";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import styled, { keyframes } from "styled-components";
import { accent } from "../../../config/colors";
import { useSelector } from "../../../store/hooks";
import {
  exportPDFRecommendationsActions,
  loadRecommendationsExcelActions,
} from "../../../store/recommendations-page/recommendationsPage";
import { AppDispatch } from "../../../store/store";
import { transformRecommendationsForExcel } from "../../../utils/excel";
import { defaultCols } from "../cols";
import { TableSpinner } from "../../../components/TableSpinner";

type Props = {
  refExcelExport: ExcelExport;
};

export const ExportMenu = ({ refExcelExport }: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const taxonomy = useSelector((s) => s.taxonomy);
  const columns = useSelector((s) => s?.recommendationsPage?.columns);
  const userRole = useSelector((s) => s.userRole.userRole);

  const exportPDFStatus = useSelector(
    (s) => s.recommendationsPage.exportPDFStatus,
  );
  const loadRecommendationsExcelStatus = useSelector(
    (s) => s?.recommendationsPage?.loadRecommendationsExcelStatus,
  );

  const colsMap = defaultCols(userRole).reduce((acc, item) => {
    acc[item.key] = item;
    return acc;
  }, {});

  const isPDFLoading = exportPDFStatus === "loading";
  const isExcelLoading = loadRecommendationsExcelStatus === "loading";
  const isAnyLoading = isPDFLoading || isExcelLoading;

  const handleExportPDF = () => {
    dispatch(exportPDFRecommendationsActions.trigger());
  };

  const handleExportExcel = () => {
    dispatch(
      loadRecommendationsExcelActions.trigger({
        onSuccess: (res) => {
          const data = transformRecommendationsForExcel({
            recommendations: res.value,
            columns: columns.map((col) => {
              return {
                ...col,
                excel: colsMap[col.key].excel,
              };
            }),
            t,
            taxonomy,
          });
          refExcelExport?.save(data);
        },
        onError: () => {},
      }),
    );
  };

  const menuProps: IContextualMenuProps = {
    items: [
      {
        key: "exportPDF",
        text: t("greco.exportPDF"),
        iconProps: {
          iconName: isPDFLoading ? "ProgressRingDots" : "PDF",
          style: { color: accent.red },
        },
        disabled: isPDFLoading,
        onClick: handleExportPDF,
      },
      {
        key: "exportExcel",
        text: "Export Excel",
        iconProps: {
          iconName: isExcelLoading ? "ProgressRingDots" : "ExcelDocument",
          style: { color: "green" },
        },
        disabled: isExcelLoading,
        onClick: handleExportExcel,
      },
    ],
  };

  return (
    <>
      <StyledCommandBarButton
        text="Export"
        iconProps={{ iconName: "Export" }}
        menuProps={menuProps}
        disabled={isAnyLoading}
      />
      {isAnyLoading && <TableSpinner />}
    </>
  );
};

const spin = keyframes`
  from {
    transform: rotateZ(0deg)
  }
  to {
    transform: rotateZ(360deg)
  }
`;

const StyledCommandBarButton = styled(CommandBarButton)`
  height: 32px;
  &.isLoading {
    i {
      animation: ${spin} 500ms linear infinite;
    }
  }
`;
